/// Port of mobile/lib/user/ui/change_password_screen.dart.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { appRoutes } from '@/app/router/routes';
import { useAuthStore } from '@/auth/handler/authStore';
import { useThemeColors } from '@/settings/handler/settingsStore';
import { PageChrome } from '@/ui/PageChrome';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { PixelPasswordField } from '@/ui/pixel/PixelPasswordField';

export function ChangePasswordScreen() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setPassword = useAuthStore((s) => s.setPassword);

  const isAdd = !user.hasPassword;
  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave =
    !saving && password.length >= 8 && confirm.length > 0;

  const save = async (): Promise<void> => {
    setError(null);
    if (password.length < 8) {
      setError(t('profilePasswordTooShort'));
      return;
    }
    if (password !== confirm) {
      setError(t('authResetPasswordMismatch'));
      return;
    }
    setSaving(true);
    try {
      await setPassword(password);
      navigate(appRoutes.user, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageChrome title={isAdd ? t('profileAddPassword') : t('profileChangePasswordTitle')}>
      <div className="flex-1 overflow-y-auto px-6 pb-10 pt-6">
        <p className="leading-tight" style={{ fontSize: 14, color: colors.onSurface, opacity: 0.65 }}>
          {isAdd ? t('profileAddPasswordHint') : t('authResetPasswordHint')}
        </p>

        <div className="mt-4 leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
          {t('authPassword')}
        </div>
        <div className="mt-2">
          <PixelPasswordField
            value={password}
            disabled={saving}
            autoComplete="new-password"
            onChange={(value) => {
              setPasswordValue(value);
              if (error) setError(null);
            }}
          />
        </div>

        <div className="mt-3 leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
          {t('authResetConfirmPassword')}
        </div>
        <div className="mt-2">
          <PixelPasswordField
            value={confirm}
            disabled={saving}
            autoComplete="new-password"
            onChange={(value) => {
              setConfirm(value);
              if (error) setError(null);
            }}
          />
        </div>

        {error && (
          <p className="mt-2 leading-tight" style={{ fontSize: 16, color: '#ef5350' }}>
            {error}
          </p>
        )}

        <PixelContainer
          radius={3}
          pixelScale={2}
          padding="12px 0"
          backgroundColor={canSave ? colors.onSurface : colors.surfaceContainerHighest}
          borderColor={canSave ? colors.onSurface : colors.outline}
          className="mt-3 w-full"
          onClick={canSave && !saving ? () => void save() : undefined}
        >
          <div className="flex justify-center" style={{ minHeight: 22 }}>
            {saving ? (
              <span
                className="inline-block h-[18px] w-[18px] animate-spin rounded-full border-2"
                style={{ borderColor: colors.surface, borderTopColor: 'transparent' }}
              />
            ) : (
              <span
                className="leading-none"
                style={{
                  fontSize: 22,
                  color: canSave ? colors.surface : colors.onSurface,
                  opacity: canSave ? 1 : 0.4,
                }}
              >
                {isAdd ? t('profileAddPassword') : t('profileSavePassword')}
              </span>
            )}
          </div>
        </PixelContainer>
      </div>
    </PageChrome>
  );
}
